import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { FeedCommentDto, CommentQueryParams, CreateCommentDto } from '@/api/comment';
import { useEventBus } from '@/hooks/use-event-bus';

// Event name for feed comment status changes
const FEED_COMMENT_EVENT = 'feed-comment-status-change';

// Event payload type
interface FeedCommentEvent {
  feedId: string;
  commentCount: number;
}

/**
 * Hook for managing comments for a feed
 */
export const useComments = (feedId?: string, clearCommentForm?: () => void) => {
  const [comments, setComments] = useState<FeedCommentDto[]>([]);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState<FeedCommentDto | null>(null);

  // Initialize event bus for comment status changes
  const { emit } = useEventBus<FeedCommentEvent>(FEED_COMMENT_EVENT);

  // Pagination constants
  const COMMENTS_PAGE_SIZE = 10;

  // Fetch initial comments for the feed
  const fetchComments = useCallback(async () => {
    if (!feedId) return;

    try {
      setIsCommentsLoading(true);
      const params: CommentQueryParams = {
        order: 'DESC',
        field: 'id',
        size: COMMENTS_PAGE_SIZE
      };
      const response = await api.feedComment.getFeedCommentsWithPagination(feedId, params);
      const newComments = response.list || [];
      setComments(newComments);
      setHasMoreComments(newComments.length === COMMENTS_PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsCommentsLoading(false);
    }
  }, [feedId]);

  // Load more comments
  const loadMoreComments = useCallback(async () => {
    if (!feedId || !hasMoreComments || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const lastComment = comments[comments.length - 1];
      const params: CommentQueryParams = {
        pivot: lastComment?.id,
        order: 'DESC',
        field: 'id',
        size: COMMENTS_PAGE_SIZE
      };
      const response = await api.feedComment.getFeedCommentsWithPagination(feedId, params);
      const newComments = response.list || [];

      if (newComments.length > 0) {
        setComments(prev => [...prev, ...newComments]);
        setHasMoreComments(newComments.length === COMMENTS_PAGE_SIZE);
      } else {
        setHasMoreComments(false);
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [feedId, comments, hasMoreComments, isLoadingMore]);

  // Fetch comments when feedId changes
  useEffect(() => {
    if (feedId) {
      fetchComments().then();
    }
  }, [feedId, fetchComments]);

  // Handle comment input change
  const handleChangeComment = useCallback((text: string) => {
    setNewComment(text);
  }, []);

  // Handle comment submission
  const handleSubmitComment = useCallback(async () => {
    if (!feedId || !newComment.trim()) return;

    try {
      setIsCommentSubmitting(true);
      const commentData: CreateCommentDto = { content: newComment.trim() };
      await api.feedComment.write(feedId, commentData);

      // Clear the comment form using the provided clear function
      if (clearCommentForm) {
        clearCommentForm();
      }
      setNewComment('');

      // Refresh comments after submission
      await fetchComments();

      // Emit event to notify other components about the comment count change
      emit({
        feedId: feedId,
        commentCount: comments.length + 1 // Add 1 for the new comment
      });
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsCommentSubmitting(false);
    }
  }, [feedId, newComment, fetchComments, comments.length, emit, clearCommentForm]);

  // Handle comment edit
  const handleEdit = useCallback((comment: FeedCommentDto) => {
    setEditingComment(comment);
  }, []);

  // Handle comment delete
  const handleDelete = useCallback(async (commentId: number) => {
    if (!feedId) return;

    try {
      await api.feedComment.delete(feedId, commentId);
      // Refresh comments after deletion
      await fetchComments();

      // Emit event to notify other components about the comment count change
      emit({
        feedId: feedId,
        commentCount: Math.max(0, comments.length - 1) // Subtract 1 for the deleted comment
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  }, [feedId, fetchComments, comments.length, emit]);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
  }, []);

  // Handle editing comment change
  const handleChangeEditingComment = useCallback((text: string) => {
    if (editingComment) {
      setEditingComment({ ...editingComment, content: text });
    }
  }, [editingComment]);

  // Handle edit comment
  const handleUpdateComment = useCallback(async () => {
    if (!feedId || !editingComment) return;

    try {
      setIsCommentSubmitting(true);
      await api.feedComment.update(feedId, editingComment);
      setEditingComment(null);
      // Refresh comments after edit
      await fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsCommentSubmitting(false);
    }
  }, [feedId, editingComment, fetchComments]);

  return {
    comments,
    isCommentsLoading,
    isLoadingMore,
    hasMoreComments,
    newComment,
    isCommentSubmitting,
    editingComment,
    onChangeComment: handleChangeComment,
    onSubmitComment: handleSubmitComment,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onCancelEdit: handleCancelEdit,
    onUpdateComment: handleUpdateComment,
    onChangeEditingComment: handleChangeEditingComment,
    onLoadMoreComments: loadMoreComments,
  };
};
